import { useMutation, useQueryClient } from "react-query";

import { updateGoal } from "api/goals";
import type { Goal } from "api/types/aba";
import { useGlobalToast } from "components/GlobalToastProvider";
import { GET_GOAL_BY_ID, GET_LIST_OF_GOALS } from "@constants/reactQueryKeys";

interface UpdateGoalPayload {
  goalId: number;
  clientId: string;
  goalData: Partial<Goal>;
}

const useUpdateGoal = (options: any = {}) => {
  const { onSuccess = () => {}, onError = () => {}, ...otherOptions } = options;
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const {
    mutate: updateGoalAction,
    isLoading: isUpdatingGoal,
    ...rest
  } = useMutation(({ goalId, goalData }: UpdateGoalPayload) => updateGoal(goalId, goalData), {
    onSuccess: (response, variables) => {
      showToast({ message: "Goal updated successfully" });

      queryClient.invalidateQueries([GET_LIST_OF_GOALS, variables.clientId]);
      queryClient.invalidateQueries([GET_GOAL_BY_ID, variables.goalId]);

      onSuccess(response, variables);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || "Failed to update goal";
      showToast({ message: errorMessage, errorState: true });

      onError(error);
    },
    ...otherOptions,
  });

  return {
    updateGoalAction,
    isUpdatingGoal,
    ...rest,
  };
};

export default useUpdateGoal;
